import axios from 'axios';
import Apicall from "./Apicall";
import * as env from '../env';
import { markTwoFactorVerified, clearTwoFactorVerified, isTwoFactorVerifiedCached } from "./twoFactorState";

export { markTwoFactorVerified };
export interface currentUserInterface {
    firstname: string;
    lastname: string;
    email: string;
    role: string;
    id: number;
    hasCitizen?: boolean;
    city_assistant_actif?: boolean;
    alertTypes: string[];
    unseen_signalements_count?: number;
    unseen_permit_requests_count?: number;
}
// AuthGuard relance un check à chaque navigation (nouvel élément <AuthGuard>
// monté à chaque changement de route) : sans ce cache, ça faisait un aller-
// retour /auth/ping — et un flash blanc le temps de la réponse — à chaque
// clic dans le menu. Le cache est invalidé au logout (voir plus bas).
const PROFILE_CACHE_TTL_MS = 30000;
let profileCache: { promise: Promise<currentUserInterface | null>; timestamp: number } | null = null;

export const getProfile = ():Promise<currentUserInterface|null> => {
    const now = Date.now();
    if (profileCache && (now - profileCache.timestamp) < PROFILE_CACHE_TTL_MS) {
        return profileCache.promise;
    }

    const promise = new Promise<currentUserInterface | null>(async(res) => {
        const apicall = new Apicall();
        apicall.call('GET','auth/ping').then((pingresponse) => {
            if(pingresponse.status !== "OK"){
                res(null);
                return;
            }
            res(pingresponse.data.user);
        }).catch(() =>{
            res(null);
        });
    });

    profileCache = { promise, timestamp: now };
    return promise;
}

export const clearProfileCache = ():void => {
    profileCache = null;
}

export interface twoFactorStatusInterface {
    required: boolean;
    confirmed: boolean;
    method: string | null;
    token_verified: boolean;
    locked_until: string | null;
}

// Une fois le jeton courant vérifié, il le reste pour toute sa durée de vie
// côté serveur (cache jusqu'à 12h) — inutile de rappeler /me/2fa/status à
// chaque navigation une fois que c'est acquis pour ce jeton. Réinitialisé à
// chaque connexion/déconnexion (nouveau jeton = à revérifier) — et aussi
// par Apicall.ts dès qu'une réponse renvoie `two_factor_required` : ce
// drapeau n'a pas de TTL propre, donc s'il devient faux côté serveur
// (jeton re-émis, 2FA reconfiguré...) avant que le drapeau client ne soit
// explicitement effacé, un admin voyait une erreur "2FA requise" sans
// issue sur une simple sauvegarde de formulaire (14 octobre 2026).
export const clearTwoFactorCache = clearTwoFactorVerified;

export const getTwoFactorStatus = async (): Promise<twoFactorStatusInterface | null> => {
    if (isTwoFactorVerifiedCached()) {
        return { required: true, confirmed: true, method: null, token_verified: true, locked_until: null };
    }
    const apicall = new Apicall();
    const response = await apicall.call('GET', 'me/2fa/status');
    if (response.status !== 'OK') {
        return null;
    }
    if (response.data.token_verified) {
        markTwoFactorVerified();
    }
    return response.data as twoFactorStatusInterface;
}

interface errorInterface {
    errorcode:string;
    errormessage:string;
}
class Auth {
    errorcode:string = '';
    errormessage:string = '';

    /**
     * `city_id` est optionnel : la console municipale l'envoie (le compte
     * doit appartenir à la ville du sous-domaine), l'espace Super Admin s'en
     * passe. `users.email` étant unique sur toute la table, l'adresse suffit
     * à résoudre le compte. Ne JAMAIS envoyer la clé quand la ville est
     * inconnue : `/auth/login` la transforme en condition sur `users`, et
     * une ville qui ne correspond pas fait échouer la connexion avec le
     * message générique d'identifiants invalides.
     */
    public async login(email:string, password: string, city_id?: number | null):Promise<boolean> {
        this.clearErrMsg();
        axios.defaults.headers.common['Accept'] = 'application/json';
        axios.defaults.headers.common['Accept-Language'] = 'fr';
        const donnees: {email: string, password: string, city_id?: number} = {email, password};
        if (city_id) {
            donnees.city_id = city_id;
        }
        return axios.post(env.apiUrl + `/api/auth/login`, donnees).then((response) => {
            if(response.data.status === 'ERROR') {
                this.errorcode = 'ERR000';
                this.errormessage = response.data.message;
                localStorage.removeItem('token');
                return false;
            }
            localStorage.setItem('token', response.data);
            // Sinon un getProfile() récent (ex: AuthGuard sur '/' avant la
            // redirection vers /login) reste en cache jusqu'à 30s avec son
            // résultat "non connecté" — l'AuthGuard qui suit immédiatement
            // ce login le relit tel quel et renvoie à /login, alors que la
            // connexion a réussi. Symétrique du clearProfileCache() déjà
            // fait au logout().
            clearProfileCache();
            // Nouveau jeton : sa vérification 2FA (si le compte en a besoin)
            // repart de zéro, même si le jeton précédent l'était.
            clearTwoFactorCache();
            return true;
        }).catch((err:any) => {
            localStorage.removeItem('token');
            this.errorcode = 'ERR000';
            // err.response est absent pour un échec réseau (pas de réponse
            // HTTP du tout — CORS, DNS, timeout) : sans cette garde, ça
            // plantait le formulaire de connexion au lieu d'afficher un
            // message d'erreur.
            this.errormessage = err.response?.data?.message ?? 'Un problème est survenu. Veuillez réessayer.';
            return false;
        });

    }

    public getLastError():errorInterface {
        return {errorcode: this.errorcode, errormessage: this.errormessage}
    }

    private clearErrMsg():void {
        this.errorcode = '';
        this.errormessage = '';
    }

    public async logout():Promise<boolean> {
        // Révoque le token côté serveur avant de l'oublier localement —
        // sinon un token volé restait valide indéfiniment après logout.
        // Apicall.call() ne rejette jamais (erreurs capturées en interne),
        // donc rien à essayer/attraper ici même si le token est déjà expiré.
        const apicall = new Apicall();
        await apicall.call('POST', 'auth/logout');
        localStorage.removeItem('token');
        // Sinon le profil mis en cache resterait "connecté" jusqu'à 30s
        // après la déconnexion pour un nouvel AuthGuard monté entretemps.
        clearProfileCache();
        clearTwoFactorCache();
        return true;
    }
}

export default Auth;
